import { snakeCase, startCase } from 'es-toolkit/compat';
import { MouseEvent, useEffect } from 'react';
import { getAuthRole, getCarrierGuid } from 'shared/auth/AuthToken';
import { logError } from 'shared/helpers/Logger';
import { openExternalURL } from 'shared/helpers/URLHelpers';
import { parseSearchQuery } from 'shared/utils/URLUtils';

declare global {
  interface Window {
    // Loaded by the Heap device mode destination of Segment.
    heap?: {
      addEventProperties: (properties: Record<string, unknown>) => void;
      addUserProperties: (properties: Record<string, unknown>) => void;
      removeEventProperty: (property: string) => void;
    };
  }
}

function tryRun(fn: () => void, fallback?: () => void): void {
  if (typeof analytics != 'undefined') {
    try {
      fn();
      return;
    } catch (error: unknown) {
      logError(error, 'Analytics');
    }
  }

  if (fallback) {
    try {
      fallback();
    } catch (error: unknown) {
      logError(error, 'Analytics');
    }
  }
}

let heapCarrierGuid: string | null | undefined;

/**
 * Heap autocaptures its own events (e.g. "Click Any Element", pageviews),
 * which do not carry the properties of the Segment `track` calls. Registering
 * `carrier_guid` on Heap itself makes it visible on every event of a session.
 * */
function syncHeapCarrierGuid(): boolean {
  // Nothing is cached while Heap is missing, leaving a later call able to
  // register.
  if (window.heap) {
    const carrierGuid = getCarrierGuid();

    if (carrierGuid !== heapCarrierGuid) {
      try {
        if (carrierGuid) {
          window.heap.addEventProperties({ carrier_guid: carrierGuid });
          window.heap.addUserProperties({ carrier_guid: carrierGuid });
        } else {
          window.heap.removeEventProperty('carrier_guid');
        }

        heapCarrierGuid = carrierGuid;
      } catch (error: unknown) {
        logError(error, 'Analytics');
      }
    }
  }

  return heapCarrierGuid != null;
}

/**
 * Drops the Heap registration so events fired after a sign out are not
 * attributed to the previous carrier.
 *
 * The cache is reset to `undefined`, meaning "unknown", so the next sync
 * re-registers instead of trusting what this module last wrote.
 * */
export function clearHeapCarrierGuid(): void {
  heapCarrierGuid = undefined;

  try {
    window.heap?.removeEventProperty('carrier_guid');
  } catch (error: unknown) {
    logError(error, 'Analytics');
  }
}

const HEAP_SYNC_INTERVAL = 50;
const HEAP_SYNC_MAX_ATTEMPTS = 100;

/**
 * Registers with Heap as early as possible, because properties added after Heap
 * captures an event never reach it — `analytics.ready` already misses the
 * landing pageview.
 *
 * Called from the app entry points, never at import time, so that importing
 * this module stays free of timers. Polling continues until a guid is
 * registered, which also covers a fresh login, where the cookie is only written
 * once the settings resolve.
 * */
export function initHeapCarrierGuidSync(): void {
  if (syncHeapCarrierGuid()) {
    return;
  }

  let attempts = 0;

  const timer = window.setInterval(() => {
    attempts++;

    if (syncHeapCarrierGuid() || attempts >= HEAP_SYNC_MAX_ATTEMPTS) {
      window.clearInterval(timer);
    }
  }, HEAP_SYNC_INTERVAL);
}

/**
 * Ensures that every analytics payload carries the `carrier_guid` property.
 *
 * An explicitly passed guid wins, because the call site can know a carrier the
 * session does not — admin impersonation and subhaul contexts, for example.
 * The session carrier is only the fallback, which keeps the property present
 * even when the caller passes nothing or an unresolved `undefined`.
 * */
function withCarrierGuid(
  properties: Record<string, unknown> = {},
): Record<string, unknown> {
  return {
    ...properties,
    carrier_guid: properties.carrier_guid ?? getCarrierGuid() ?? null,
  };
}

export function onAnalyticsReady(fn: () => void): void {
  tryRun(() => {
    analytics.ready(() => {
      fn();
    });
  });
}

export function identifyAnalytics(user?: {
  guid: string;
  hash?: string;
  accountType?: 'owner_operator' | 'owner_operator_pro' | 'fleet' | null;
  experiment_group?: number[] | null;
  sd_importer_installed?: boolean | null;
  company?: {
    id?: string; //id is required for intercom
    guid?: string;
    name?: string;
    email?: string;
    state?: string;
    usdot?: string;
    is_super?: boolean;
    superpay_onboarding_status?: string;
    is_fmcsa_phone_verified?: boolean;
  };
  enabled_features?: string[];
}): void {
  onAnalyticsReady(() => {
    // The only point the carrier context changes: sign in, carrier switch and
    // sign out all reach Heap from here.
    syncHeapCarrierGuid();

    const authRole = getAuthRole();

    if (user) {
      const companyType =
        user.accountType == null
          ? undefined
          : user.accountType === 'fleet'
            ? 'Fleet'
            : 'Owner Operator';

      const { company } = user;

      analytics.identify(
        user.guid,
        {
          carrier_guid: getCarrierGuid(),
          company: {
            ...company,
            superpay_onboarding_status:
              company?.superpay_onboarding_status ?? null,
            type: companyType,
          },
          experiment_group: user.experiment_group,
          //intercom has a problem with parsing array,
          // that's why we are sending duplicate property as a string
          experiment_group_list: user.experiment_group?.toString(),
          sd_importer_installed: user.sd_importer_installed,
          enabled_features: user.enabled_features?.join(','),
        },
        !user.hash || authRole === 'admin'
          ? { integrations: { Intercom: false } }
          : ({
              Intercom: {
                user_hash: user.hash,
                company: { type: companyType },
              },
            } as SegmentAnalytics.SegmentOpts),
      );
    } else {
      try {
        // Handle errors when `analytics.user` can be undefined.
        if (analytics.user().id()) {
          const previousAnonoymousId = analytics.user().anonymousId();
          // Reset storage and track new page-view event when user is authorized.
          analytics.reset();
          // Forget what this module thinks Heap holds.
          clearHeapCarrierGuid();
          // To keep the previous anonymousId and do duplicate the same device.
          analytics.setAnonymousId(previousAnonoymousId);
        }
      } catch (error: unknown) {
        analytics.identify();
        logError(error, 'Analytics');
      }
    }

    analytics.page(
      withCarrierGuid({
        user_guid: user?.guid,
        ...getUTMTags(),
      }),
    );
  });
}

const initialUTMTags = getUTMTags('initial_');

interface TrackEventIntegrations {
  Intercom?: boolean;
}

export function trackEvent(
  event: string,
  properties: Record<string, unknown> = {},
  callback?: () => void,
  integrations?: TrackEventIntegrations,
): void {
  const queryUtmTags = getUTMTags();

  properties = withCarrierGuid({
    ...initialUTMTags,
    ...queryUtmTags,
    ...properties,
    utm_source:
      properties.utm_source || queryUtmTags.utm_source || chooseUTMSource(),
  });

  if (import.meta.env.MODE !== 'production') {
    logEvent(event, properties);
    lintEvent(event, properties);
  }

  tryRun(
    () => {
      if (integrations) {
        analytics.track(event, properties, { integrations }, callback);
        return;
      }

      analytics.track(event, properties, callback);
    },
    () => {
      callback?.();

      if (import.meta.env.MODE !== 'production') {
        // eslint-disable-next-line no-console
        console.debug('%s (%j)', event, properties);
      }
    },
  );
}

/**
 * SuperCard events stay on Segment and its other destinations. Intercom is
 * omitted because the workspace is at its active event-name limit.
 */
export function trackSuperCardEvent(
  event: string,
  properties: Record<string, unknown> = {},
  callback?: () => void,
): void {
  trackEvent(event, properties, callback, { Intercom: false });
}

export function trackAnchorClick(
  event: string,
  properties: Record<string, unknown>,
  mouseEvent: MouseEvent<HTMLAnchorElement>,
) {
  if (
    // Capture only left clicks.
    mouseEvent.button !== 0 ||
    // Do not capture prevented clicks.
    mouseEvent.defaultPrevented
  ) {
    return;
  }

  const { href, target } = mouseEvent.currentTarget;

  const shouldWaitTrackComplete =
    // Skip links without href.
    !!href &&
    // Skip clicks with modifiers.
    !mouseEvent.altKey &&
    !mouseEvent.ctrlKey &&
    !mouseEvent.metaKey &&
    !mouseEvent.shiftKey &&
    // Wait only for links to current window.
    (!target || target === '_self');

  if (shouldWaitTrackComplete) {
    mouseEvent.preventDefault();
  }

  trackEvent(event, properties, () => {
    if (shouldWaitTrackComplete) {
      openExternalURL(href);
    }
  });
}

function trackPage(name: string, properties: Record<string, unknown> = {}) {
  properties = withCarrierGuid(properties);

  tryRun(
    () => {
      analytics.page(name, properties);
    },
    () => {
      if (import.meta.env.MODE !== 'production') {
        // eslint-disable-next-line no-console
        console.debug('trackPage %j', name);
      }
    },
  );
}

export function usePageTrack(name: string) {
  useEffect(() => {
    trackPage(name);
  }, [name]);
}

/**
 * @see https://www.notion.so/superdispatch/Event-and-Properties-Naming-Guidelines-15f21c5508074091869c6c0dc18e0564
 * */
function lintEvent(eventName: string, properties: Record<string, unknown>) {
  const prefix = eventName.slice(0, eventName.indexOf(':'));
  const body = eventName.slice(eventName.indexOf(':') + 1).trim();
  const errors = [];

  /**
   * Good: "CTMS: Clicked Driver"
   * Bad:  "Clicked Driver"
   * */
  if (!['SLB', 'CTMS'].includes(prefix)) {
    errors.push(
      `Expected event name prefix to be "SLB:" or "CTMS:" but got "${prefix}."`,
    );
  }

  /**
   * Event body should be in Proper Case.
   *
   * Good: "CTMS: Clicked Driver"
   * Bad:  "CTMS: clicked driver"
   * */
  if (startCase(body) !== body) {
    errors.push(`Expected event name to be in Proper Case but got "${body}".`);
  }

  /**
   * Checks weather event body has "a" or "an" articles.
   *
   * Good: "CTMS: Clicked Driver"
   * Bad:  "CTMS: Clicked A Driver"
   * */
  if (/\san?\s/i.exec(body)) {
    errors.push('Expected event body to not have articles (.e.g "a", "an").');
  }

  {
    /**
     * Event body should start with past tense verb.
     *
     * Good: "CTMS: Clicked Driver"
     * Bad:  "CTMS: Click Driver"
     * */
    const [verb] = body.split(/\s/);

    if (!verb?.endsWith('ed')) {
      errors.push(
        `Expect event body to start with past tense verb but got "${verb}".`,
      );
    }
  }

  /**
   * Good: { driver_guid: "sj248d" }
   * Bad:  { driverGuid: "sj248d" }
   * */
  {
    const errorKeys = Object.keys(properties).filter((k) => k !== snakeCase(k));

    if (errorKeys.length) {
      errors.push(
        `Expected event properties keys to be in snake_case but got ${errorKeys
          .map((k) => `"${k}"`)
          .join(', ')}`,
      );
    }
  }

  if (errors.length) {
    // eslint-disable-next-line no-console
    console.groupCollapsed(
      '%cAnalytics: Found some issues for event:\n "%s"',
      'color: green',
      eventName,
    );
    for (const error of errors) {
      // eslint-disable-next-line no-console
      console.log(error);
    }
    // eslint-disable-next-line no-console
    console.info('Properties: %O', properties);
    // eslint-disable-next-line no-console
    console.info(
      'Link to the docs https://www.notion.so/superdispatch/Event-and-Properties-Naming-Guidelines-15f21c5508074091869c6c0dc18e0564',
    );
    // eslint-disable-next-line no-console
    console.groupEnd();
  }
}

function logEvent(eventName: string, properties: Record<string, unknown>) {
  // eslint-disable-next-line no-console
  console.groupCollapsed('Analytics: "%s"', eventName);
  // eslint-disable-next-line no-console
  console.info('Properties: %O', properties);
  // eslint-disable-next-line no-console
  console.groupEnd();
}

export type UTMSource = 'Web CTMS' | 'Mobile CTMS' | 'Web SLB' | 'Mobile SLB';
export function chooseUTMSource(): UTMSource {
  if (import.meta.env.VITE_APP_NAME === 'ctms') {
    return 'Web CTMS';
  }

  if (import.meta.env.VITE_APP_NAME === 'ctmsw') {
    return 'Mobile CTMS';
  }

  if (import.meta.env.VITE_APP_NAME === 'slbd') {
    return 'Web SLB';
  }

  return 'Mobile SLB';
}

function getUTMTags(prefix = '') {
  const query = parseSearchQuery(window.location.search);
  const tags: Record<string, unknown> = {};

  Object.keys(query).forEach((key) => {
    const value = query[key];
    if (key.startsWith('utm_') && value) {
      tags[prefix + key] = decodeURIComponent(value);
    }
  });

  return tags;
}

export function getInitialSource() {
  return initialUTMTags.initial_utm_source;
}

export function getUTMTag(name: string) {
  const tags = getUTMTags();

  if (name in tags) {
    return tags[name];
  }

  return null;
}
